/**
 * Filas de anexos pendentes do composer — vivem FORA de qualquer instância de
 * `SendMessage`, UMA por superfície. Mesmo padrão de `motor-de-voz.js`:
 * `view-chat.vue` tem DUAS instâncias mutuamente exclusivas do composer — a
 * do herói (`composerHeroi`, tela vazia) e a do rodapé (`composerRodape`,
 * conversa em andamento) — alternadas por `v-if`/`v-else` (`composerNoHeroi`).
 * Trocar de uma pra outra DESTRÓI a instância antiga e MONTA uma nova.
 *
 * ★ Bug medido (05/09): um anexo pendente ANTES da primeira mensagem de uma
 * conversa NOVA morria na troca. Fluxo: conversa nova (estado vazio,
 * `composerHeroi` montado) → anexa um arquivo (`arquivos`/`statusUploadPorChave`
 * do mixin de upload, ambos `data()` da instância) → primeira mensagem enviada
 * → a view troca `composerHeroi` por `composerRodape` (instância NOVA) →
 * `data()` da instância nova nasce vazia, e o anexo que ainda esperava o
 * cliente morria junto com a instância que estava SENDO DESTRUÍDA.
 *
 * ★ Desde 17/09/2026 a fila só é esvaziada por três gestos EXPLÍCITOS: o
 * envio (que leva tudo que está nela — `send-message.vue::onSend`), o "×" da
 * miniatura, e a troca/descarte de conversa (`resetarFilaDeAnexos`). Um anexo
 * que ainda não pode subir (`sem_cliente`/`erro`) TRAVA o envio em vez de
 * viajar junto — por isso não existe mais item "preservado através do envio".
 *
 * ★ Por que um REGISTRO por chave, e não um singleton único: com uma fila só,
 * um PDF anexado sem cliente em foco na `/ia` aparecia também no drawer do
 * Fee-Based (que monta o MESMO `SendMessage`), liberava o envio com texto
 * vazio e, quando o `idClienteAnexo` de lá resolvia, subia a carteira para
 * OUTRO cliente. Cada superfície pede a sua fila pela chave
 * (`chaveDaFilaDeAnexos` em `send-message-anexos-upload-mixin.js`): sem chave,
 * a fila é da instância e morre com ela; `view-chat.vue` passa uma chave
 * própria às duas instâncias e a zera ao trocar de conversa e ao ser destruída.
 *
 * ★ `geracao` é o que protege a fila de um upload que TERMINA depois do reset:
 * anexar com o cliente A → upload em voo → trocar para a conversa B (reset) →
 * o `.then` do upload gravaria `enviado` + `uploadId` da carteira de A na fila
 * NOVA, e reanexar o mesmo `File` em B mandaria a carteira de A no turno de B.
 * Quem dispara o upload captura a geração antes e confere no desfecho.
 *
 * Reativo via um `Vue` isolado — mesmo padrão do `GlobalBus`
 * (`@services/bus`) e de `motorDeVoz`: qualquer instância de `SendMessage`
 * que leia estes campos (via computed) reage à mudança automaticamente,
 * inclusive uma instância recém-montada — ela "encontra" a fila já em
 * andamento, sem perder nada.
 *
 * Criar é `obterOuCriarFila` (o `created()` do mixin); `filaDeAnexos` é só
 * LEITURA — o mixin a consome como computed, e um getter com `Vue.set` dentro
 * é efeito colateral onde só deveria haver lookup.
 */
import Vue from 'vue'

const registro = new Vue({
  data() {
    return {
      // chave da superfície → fila (ver `novaFila`)
      filas: {},
    }
  },
})

function novaFila() {
  return {
    // Os `File` do browser ainda na fila (enviados ou não).
    arquivos: [],
    // chave (`chaveDoArquivo`) → 'enviando' | 'enviado' | 'erro' | 'sem_cliente'
    statusUploadPorChave: {},
    // chave → { uploadId, nome, contentType, tamanho } (só os 'enviado')
    uploadPorChave: {},
    // chave → mensagem REAL do backend, ou o texto genérico
    mensagemErroPorChave: {},
    /**
     * ★ [spec-009 T013] `[{uploadId, nome}]` dos anexos que subiram pelo
     * canal de ANÁLISE **sem cliente** e ainda não têm dono.
     *
     * Lista à parte das três acima de propósito: ela **sobrevive ao envio**.
     * `arquivos`/`uploadPorChave` descrevem o que ainda está na caixa, e o
     * envio os esvazia (`removerAnexosEnviados`) — mas o momento em que o
     * cliente faz falta costuma ser DEPOIS que a mensagem partiu, quando a
     * IA lê o arquivo e descobre que precisa de um cadastro para concluir.
     * Guardar só o que está na fila deixaria justamente esse caso de fora, e
     * o consultor teria de reanexar o que já subiu (US-3: "sem exigir
     * reanexar o arquivo").
     *
     * Sai daqui quando o anexo ganha dono — vinculado agora (200) ou já
     * pertencente a outro cliente (409, o servidor nomeia). O reset da fila
     * (troca/descarte de conversa) leva a lista junto.
     */
    aguardandoCliente: [],
    // Sobe a cada reset/descarte — um upload em voo confere antes de gravar.
    geracao: 0,
  }
}

/** Cria a fila da superfície `chave` (reativa) se ainda não existe, e a devolve. */
function obterOuCriarFila(chave) {
  if (!registro.filas[chave]) Vue.set(registro.filas, chave, novaFila())
  return registro.filas[chave]
}

/**
 * A fila da superfície `chave` — só LEITURA. Chave desconhecida devolve uma
 * fila vazia SOLTA (não registrada): quem precisa escrever cria antes com
 * `obterOuCriarFila`.
 */
function filaDeAnexos(chave) {
  return registro.filas[chave] || novaFila()
}

/** Zera a fila da superfície `chave` mantendo o objeto — quem já a lê reage. */
function resetarFilaDeAnexos(chave) {
  const fila = registro.filas[chave]
  if (!fila) return
  Object.assign(fila, novaFila(), { geracao: fila.geracao + 1 })
}

/**
 * Descarta TODAS as filas de uma superfície (o prefixo é o `_uid` dela).
 *
 * ⚠️ [20/09/2026] Existe porque a chave passou a levar a conversa viva: o
 * `beforeDestroy` da view descartava só a chave CORRENTE, e as filas das
 * outras abas — com os `File` dentro, até 10 MB cada — ficavam no registro sem
 * ninguém que as alcançasse (o `_uid` novo na volta as torna inatingíveis).
 */
function descartarFilasComPrefixo(prefixo) {
  if (!prefixo) return
  Object.keys(registro.filas).forEach((chave) => {
    if (chave.indexOf(prefixo) === 0) descartarFilaDeAnexos(chave)
  })
}

/** Solta a fila da superfície `chave` — uma instância sem chave própria morreu. */
function descartarFilaDeAnexos(chave) {
  const fila = registro.filas[chave]
  if (fila) fila.geracao += 1
  Vue.delete(registro.filas, chave)
}

/**
 * ★ [spec-009 T013] Registra um anexo de ANÁLISE que subiu sem cliente.
 *
 * Idempotente por `uploadId`: reanexar o mesmo `File` (ou um segundo upload
 * do mesmo arquivo) não duplica a entrada, e duplicata viraria chamada de
 * vínculo repetida para o mesmo anexo.
 */
function registrarAnexoAguardandoCliente(chave, anexo) {
  if (!anexo || !anexo.uploadId) return
  const fila = obterOuCriarFila(chave)
  const jaRegistrado = fila.aguardandoCliente.some(
    (item) => item.uploadId === anexo.uploadId
  )
  if (jaRegistrado) return
  fila.aguardandoCliente.push({ uploadId: anexo.uploadId, nome: anexo.nome || '' })
}

/**
 * Os anexos que esperam um cliente, em CÓPIA — nunca o array vivo do
 * registro: quem consome itera e vincula, e mutar a lista por baixo de quem
 * a percorre é o defeito que a cópia evita.
 */
function anexosAguardandoCliente(chave) {
  const fila = registro.filas[chave]
  if (!fila) return []
  return fila.aguardandoCliente.map((item) => ({
    uploadId: item.uploadId,
    nome: item.nome,
  }))
}

/** Tira da espera os anexos que já têm dono (vinculado agora, ou de outro). */
function removerAnexosAguardandoCliente(chave, uploadIds) {
  const fila = registro.filas[chave]
  if (!fila || !uploadIds || !uploadIds.length) return
  fila.aguardandoCliente = fila.aguardandoCliente.filter(
    (item) => uploadIds.indexOf(item.uploadId) === -1
  )
}

/**
 * Descarta TODAS as filas — uso EXCLUSIVO de teste. Cada arquivo `.unit.js`
 * que mexe nestas filas roda dentro do MESMO registro de módulos do Jest
 * (module cache por arquivo de teste, não por `it()`), então sem este reset
 * o estado de um `it()` vaza para o próximo dentro do mesmo arquivo — mesmo
 * padrão de `jest.clearAllTimers()`/`jest.resetModules()` já documentado no
 * repo para esse tipo de estado compartilhado.
 */
function resetarAnexosPendentes() {
  Object.keys(registro.filas).forEach(descartarFilaDeAnexos)
}

export {
  obterOuCriarFila,
  filaDeAnexos,
  resetarFilaDeAnexos,
  descartarFilaDeAnexos,
  descartarFilasComPrefixo,
  resetarAnexosPendentes,
  registrarAnexoAguardandoCliente,
  anexosAguardandoCliente,
  removerAnexosAguardandoCliente,
}
